import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { combineLatest, distinctUntilChanged, Subscription } from 'rxjs';
import { AuthorizationService } from '~ngx-shared/authentication';
import { MenuService } from '~ngx-shared/layout';
import { Aggregate } from '~ngx-shared/models';
import { ApplicationKindEnum } from '~shared/models';
import { UserService } from '~shared/services';
import { AuthorizationUtil } from '~shared/utils';

@Injectable({ providedIn: 'root' })
export class HearingMenuService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly userService = inject(UserService);
  private readonly menuService = inject(MenuService);
  private readonly apollo = inject(Apollo);

  subscriptions: Subscription[] = [];

  constructor() {
    this.destroyRef.onDestroy(() => this.subscriptions.forEach(s => s.unsubscribe()));
  }

  init(): void {
    combineLatest([
      this.userService.user$.pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged()),
      this.authorizationService.currentRole$.pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged()
      )
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([user, role]) => {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        this.subscriptions = [];
        if (!role) {
          return;
        }

        const personId = user?.person_id;
        if (personId) {
          this.subscriptions.push(
            this.apollo
              .subscribe<{ result?: { id: number }[] }>({
                query: gql`
                  subscription IsHearingCommissionMember($personId: bigint!) {
                    result: education_hearing_session_person(
                      where: { person_id: { _eq: $personId } }
                      limit: 1
                    ) {
                      id
                    }
                  }
                `,
                variables: { personId }
              })
              .subscribe(queryResult =>
                this.setGroupVisible('hearing-scoring', !!queryResult.data?.result?.length)
              )
          );
        }

        if (this.authorizationService.can(AuthorizationUtil.GROUP_INSPECTOR)) {
          this.subscriptions.push(
            this.apollo
              .subscribe<{ result?: Aggregate }>({
                query: gql`
                  subscription CountNewHearingApplications($kind: String!) {
                    result: external_application_aggregate(
                      where: { kind: { _eq: $kind }, status: { is_received: { _eq: true } } }
                    ) {
                      aggregate {
                        count
                      }
                    }
                  }
                `,
                variables: { kind: ApplicationKindEnum.HEARING }
              })
              .subscribe(queryResult =>
                this.setBadge(['/', 'hearings'], queryResult.data?.result?.aggregate?.count ?? 0)
              )
          );
        }
      });
  }

  private setBadge(routerLink: string[], count: number): void {
    const items = this.menuService.sideBarItems$.value;
    const item = this.menuService.getItemFromRoute(routerLink);
    const badge = count > 0 ? String(count) : undefined;
    if (item && item.badge !== badge) {
      item.badge = badge;
      this.menuService.sideBarItems$.next([...items]);
    }
  }

  private setGroupVisible(id: string, visible: boolean): void {
    const items = this.menuService.sideBarItems$.value;
    const group = this.menuService.getItemFromIds([id]);
    if (group && group.visible !== visible) {
      group.visible = visible;
      this.menuService.sideBarItems$.next([...items]);
    }
  }
}
